import { useState, useEffect, useCallback } from 'react'
import { Reseller } from '@crm-src/types/lead'
import resellersApiService, { CreateResellerData, UpdateResellerData } from '@crm-src/lib/resellers-api'
import usePermissions from '@crm-src/hooks/usePermissions'

export const useResellers = () => {
  const { canAccessResellers } = usePermissions()
  const resellerAccess = canAccessResellers()
  const [resellers, setResellers] = useState<Reseller[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadResellers = useCallback(async () => {
    // Reseller wall: a viewer without crm:resellers gets an empty list and we
    // never hit the endpoint (it would 403 + spam the console). The list is
    // unused because every reseller surface is hidden for them anyway.
    if (!resellerAccess) {
      setResellers([])
      setError(null)
      setIsLoading(false)
      return
    }
    try {
      setIsLoading(true)
      setError(null)
      const response = await resellersApiService.getAll()
      setResellers(response.resellers)
    } catch (err) {
      console.error('Error loading resellers:', err)
      setError('Failed to load resellers')
    } finally {
      setIsLoading(false)
    }
  }, [resellerAccess])

  const createReseller = useCallback(async (data: CreateResellerData): Promise<Reseller | null> => {
    try {
      const newReseller = await resellersApiService.create(data)
      setResellers(prev => [...prev, newReseller])
      return newReseller
    } catch (err) {
      console.error('Error creating reseller:', err)
      setError('Failed to create reseller')
      return null
    }
  }, [])

  const updateReseller = useCallback(async (id: string, data: UpdateResellerData): Promise<Reseller | null> => {
    try {
      const updatedReseller = await resellersApiService.update(id, data)
      setResellers(prev => prev.map(r => r.id === id ? updatedReseller : r))
      return updatedReseller
    } catch (err) {
      console.error('Error updating reseller:', err)
      setError('Failed to update reseller')
      return null
    }
  }, [])

  // RETHROWS. The caller (AdministrationResellersPage.confirmDeleteReseller)
  // awaits this inside a try/catch and toasts success on the happy path — so
  // swallowing the error here made that catch unreachable and turned every
  // failed delete into a green "Reseller deleted successfully" over a reseller
  // that was still there on the next reload (2026-08-30). crm-api now answers
  // 409 with a reason when it refuses; the caller renders it.
  const deleteReseller = useCallback(async (id: string) => {
    try {
      await resellersApiService.delete(id)
      setResellers(prev => prev.filter(r => r.id !== id))
    } catch (err) {
      console.error('Error deleting reseller:', err)
      setError('Failed to delete reseller')
      throw err
    }
  }, [])

  useEffect(() => {
    loadResellers()
  }, [loadResellers])

  return {
    resellers,
    isLoading,
    error,
    loadResellers,
    refreshResellers: loadResellers, // Alias for loadResellers
    createReseller,
    updateReseller,
    deleteReseller,
  }
}


